import {
  CheckTypeEnum,
  EntityTypeEnum,
  TemplateRuleCheckTargetEnum,
  TemplateRuleModel,
  TemplateRuleTypeEnum,
} from '../../../../../shared/models';
import yup from '../../../../../utils/yup-extended';
import {
  documentSettingsSchema,
  getAvailableCompanyCheckDefinitions,
  getAvailableIndividualCheckDefinitions,
  idDocumentSettingsSchema,
  idVerificationSettingsSchema,
} from '../../../../checks';
import { amlSettingsSchema } from '../../../../checks/aml/validation';
import { companyMonitoringSettingsSchema } from '../../../../checks/company-monitoring/validation';

const individualCheckDefinitions = getAvailableIndividualCheckDefinitions();
const companyCheckDefinitions = getAvailableCompanyCheckDefinitions();

type TemplateRuleSchema = yup.SchemaOf<
  Omit<
    TemplateRuleModel,
    | 'id'
    | 'template'
    | 'workspace'
    | 'workspaceId'
    | 'createdAt'
    | 'updatedAt'
    | 'checkSubtype'
  >
>;

export const templateRuleSchema = (): TemplateRuleSchema => {
  return yup
    .object({
      templateId: yup.string().uuid().required(),
      type: yup.string().oneOf(Object.values(TemplateRuleTypeEnum)).required(),

      // Activation JsonLogic
      activationJsonLogic: yup.object().jsonLogic().nullable(),

      // Deduplication (only for checks)
      deduplication: yup.boolean().when('type', {
        is: TemplateRuleTypeEnum.check,
        then: (s) => s.nullable(),
        otherwise: (s) => s.nullable().equals([null], '${path} must be null'),
      }),

      // Check
      checkTarget: yup.mixed().when('type', {
        is: TemplateRuleTypeEnum.check,
        then: (s) =>
          s.oneOf(Object.values(TemplateRuleCheckTargetEnum)).required(),
        otherwise: (s) => s.nullable().equals([null], '${path} must be null'),
      }),
      checkType: yup.mixed().when('type', {
        is: TemplateRuleTypeEnum.check,
        then: (s) =>
          s
            .when('$entityType', {
              is: EntityTypeEnum.individual,
              then: (schema) => {
                return schema.oneOf(
                  individualCheckDefinitions.map(
                    (definition) => definition.type,
                  ),
                );
              },
            })
            .when('$entityType', {
              is: EntityTypeEnum.company,
              then: (schema) => {
                return schema.oneOf(
                  companyCheckDefinitions.map((definition) => definition.type),
                );
              },
            })
            .required(),
        otherwise: (s) => s.nullable().equals([null], '${path} must be null'),
      }),
      checkSettings: yup.mixed().when('type', {
        is: TemplateRuleTypeEnum.check,
        then: (s) =>
          s

            .when('checkType', {
              is: CheckTypeEnum.aml,
              then: amlSettingsSchema().nullable(),
            })
            .when('checkType', {
              is: CheckTypeEnum.company_monitoring,
              then: companyMonitoringSettingsSchema().nullable(),
            })
            .when('checkType', {
              is: CheckTypeEnum.document,
              then: documentSettingsSchema().nullable(),
            })
            .when('checkType', {
              is: CheckTypeEnum.id_document,
              then: idDocumentSettingsSchema().nullable(),
            })
            .when('checkType', {
              is: CheckTypeEnum.id_verification,
              then: idVerificationSettingsSchema().nullable(),
            })
            /**
             * Cast as AnyObjectSchema
             * or yup typing will break the CheckDataSettingsModel inference
             * since MixedSchema is not assignable to inferred CheckDataSettingsModel
             */
            .default(null) as yup.AnyObjectSchema,
        otherwise: (s) => s.nullable().equals([null], '${path} must be null'),
      }),

      // Control
      controlId: yup.string().when('type', {
        is: TemplateRuleTypeEnum.control,
        then: (s) => s.uuid().required(),
        otherwise: (s) => s.nullable().equals([null], '${path} must be null'),
      }),
    })
    .noUnknown()
    .defined() as TemplateRuleSchema;
};
